import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { API_BASE, ApiError, get, post } from '../lib/api';
import { returnPathFrom } from '../lib/guards';
import { hardNavigation } from '../lib/hard-navigation';
import { openSession, type SessionNotOpened } from '../lib/session';
import { Button, Field, Input } from '../components/ui';
import { PasswordInput } from '../components/password-input';
import { AuthDivider, AuthLayout, AuthNotice } from './auth-layout';

interface LoginResponse {
  status: 'authenticated' | 'mfa_required' | 'password_expired';
  expires_at?: string;
  /** The account a second factor is owed for, partly hidden. Only with `mfa_required`. */
  masked_email?: string;
}

interface SsoOption {
  key: string;
  name: string;
  label: string;
}

type FieldKey = 'email' | 'password';

/** The shape the server accepts. Checked here only to say so before a round trip. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Whole seconds until `until`, ticking down; 0 once it has passed. */
function useSecondsUntil(until: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until === null) return;
    setNow(Date.now());
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= until) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [until]);
  return until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));
}

/**
 * Sign-in.
 *
 * Single sign-on comes first when the desk has it, because then it is how
 * most people get in; the password form follows for everybody else. A failed
 * sign-in never says *which* part was wrong: the server returns identical
 * wording for an unknown address and a bad password, and repeating that
 * faithfully here is what keeps the endpoint from becoming an account-
 * enumeration oracle.
 *
 * The fields are read from the form when it is submitted rather than from
 * React state. A browser that autofills saved credentials does not tell the
 * page until somebody interacts with it, so a button that waited for both
 * values in state sat greyed out over two visibly filled boxes.
 */
export function LoginRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const emailId = useId();
  const passwordId = useId();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  /**
   * Where to land after signing in.
   *
   * The router puts the page somebody was refused here, and it travels through
   * the second step, a forced password change and the SSO round trip, so an
   * expired session resumes where it left off. The server validates it again
   * on the way out; this is a convenience, not the control.
   */
  const destination = returnPathFrom(location);
  const carry = destination !== '/' ? { from: destination } : undefined;

  const providers = useQuery<{ data: SsoOption[] }, ApiError>({
    queryKey: ['sso-providers'],
    queryFn: () => get('/auth/sso/providers'),
    // The login page is the one screen that must render even when the API is
    // unhappy, so a failure here costs nothing but the buttons.
    retry: false,
    staleTime: 60_000,
  });
  const ssoOptions = providers.data?.data ?? [];
  const [ssoStarting, setSsoStarting] = useState<string | null>(null);

  // Set by the change-password screen, which cannot leave the person signed in:
  // changing a password ends every session, including the one that changed it.
  const justChanged = params.get('changed') === '1';
  const ssoError = readableSsoError(params.get('sso_error'));

  const [clientErrors, setClientErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [retryUntil, setRetryUntil] = useState<number | null>(null);
  const secondsLeft = useSecondsUntil(retryUntil);
  /** The password was accepted and still no session came of it. */
  const [notOpened, setNotOpened] = useState<SessionNotOpened | null>(null);

  const startSso = (key: string) => {
    setSsoStarting(key);
    const redirect = carry ? `?redirect=${encodeURIComponent(destination)}` : '';
    // A full navigation, not a fetch: the browser has to follow the redirect
    // to the provider and come back carrying its cookies.
    hardNavigation.assign(`${API_BASE}/auth/sso/${encodeURIComponent(key)}/start${redirect}`);
  };

  const signIn = useMutation<LoginResponse, ApiError, { email: string; password: string }>({
    mutationFn: (credentials) => post<LoginResponse>('/auth/login', credentials),
    onSuccess: async (result) => {
      if (result.status === 'password_expired') {
        await navigate('/change-password', { replace: true, ...(carry ? { state: carry } : {}) });
        return;
      }
      if (result.status === 'mfa_required') {
        // Which account the code is for goes along too, so the second step can
        // say "Signing in as …" the moment it opens.
        const state = { ...carry, ...(result.masked_email ? { account: result.masked_email } : {}) };
        await navigate('/verify', { replace: true, ...(Object.keys(state).length > 0 ? { state } : {}) });
        return;
      }
      // No navigation from here: the session arriving is what moves them on.
      // RedirectIfSignedIn, around this page, sends them to `destination` the
      // moment it does; navigating here as well raced it and sent them back
      // through this page on the way (see `openSession`).
      setNotOpened(await openSession(queryClient));
    },
    onError: (error) => {
      if (error.code === 'rate_limited') {
        setRetryUntil(Date.now() + (error.retryAfter ?? 60) * 1000);
        return;
      }
      const refused = error.fieldErrors;
      if (refused.email) {
        emailRef.current?.focus();
      } else if (refused.password || error.code === 'invalid_credentials') {
        // The address is usually right and the password usually is not:
        // ready to retype, without throwing away what was there.
        passwordRef.current?.focus();
        passwordRef.current?.select();
      }
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = String(data.get('email') ?? '').trim();
    const password = String(data.get('password') ?? '');

    const found: Partial<Record<FieldKey, string>> = {};
    if (!email) found.email = 'Enter your email address.';
    else if (!EMAIL_PATTERN.test(email)) found.email = 'Enter a full email address, like name@company.com.';
    if (!password) found.password = 'Enter your password.';

    signIn.reset();
    setNotOpened(null);
    setClientErrors(found);
    if (found.email) return emailRef.current?.focus();
    if (found.password) return passwordRef.current?.focus();
    signIn.mutate({ email, password });
  };

  const error = signIn.error;
  const serverFields = error?.fieldErrors ?? {};
  const fieldErrors: Partial<Record<FieldKey, string>> = {
    email: clientErrors.email ?? serverFields.email,
    password: clientErrors.password ?? serverFields.password,
  };
  const unplaced = error?.details?.find((detail) => detail.path !== 'email' && detail.path !== 'password');
  const showError = error !== null && (!error.details?.length || unplaced !== undefined);
  const limited = error?.code === 'rate_limited';

  const clearFieldError = (key: FieldKey) => {
    if (clientErrors[key]) setClientErrors((previous) => ({ ...previous, [key]: undefined }));
  };

  return (
    <AuthLayout
      title="Aurora Service Desk"
      subtitle="Sign in to continue"
      footer={<p>Can’t sign in? Contact your IT administrator.</p>}
    >
      <div className="space-y-4">
        {justChanged && !error && (
          <AuthNotice tone="positive">
            <p>Password changed. Sign in with the new one.</p>
          </AuthNotice>
        )}

        {ssoError && !error && (
          <AuthNotice tone="critical">
            <p>{ssoError}</p>
          </AuthNotice>
        )}

        {notOpened && !error && (
          <AuthNotice tone="critical" reference={notOpened.reference}>
            <p>
              {notOpened.reason === 'not_kept'
                ? 'Your password was accepted, but this browser didn’t keep you signed in. Check that cookies are allowed for this site, then try again.'
                : 'Your password was accepted, but your account couldn’t be loaded. Try again in a moment.'}
            </p>
          </AuthNotice>
        )}

        {showError && error && (
          <AuthNotice tone="critical" reference={error.isServerFault ? error.requestId : undefined}>
            {limited ? (
              <p>
                {secondsLeft > 0
                  ? `Too many sign-in attempts. Try again in ${secondsLeft} second${secondsLeft === 1 ? '' : 's'}.`
                  : 'You can try signing in again now.'}
              </p>
            ) : (
              <p>{unplaced?.message ?? error.message}</p>
            )}
          </AuthNotice>
        )}

        {ssoOptions.length > 0 && (
          <>
            <div className="space-y-2">
              {ssoOptions.map((option) => (
                <Button
                  key={option.key}
                  variant="primary"
                  size="lg"
                  className="w-full justify-center"
                  loading={ssoStarting === option.key}
                  disabled={ssoStarting !== null && ssoStarting !== option.key}
                  onClick={() => startSso(option.key)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            <AuthDivider>or use your email</AuthDivider>
          </>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <Field label="Email address" htmlFor={emailId} error={fieldErrors.email} required>
            <Input
              ref={emailRef}
              id={emailId}
              name="email"
              type="email"
              // An address is written left to right whatever the language.
              dir="ltr"
              autoComplete="username"
              autoFocus
              required
              onChange={() => clearFieldError('email')}
              aria-invalid={fieldErrors.email ? true : undefined}
              aria-describedby={fieldErrors.email ? `${emailId}-error` : undefined}
              placeholder="you@company.com"
            />
          </Field>

          <Field label="Password" htmlFor={passwordId} error={fieldErrors.password} required>
            <PasswordInput
              ref={passwordRef}
              id={passwordId}
              name="password"
              autoComplete="current-password"
              required
              onChange={() => clearFieldError('password')}
              aria-invalid={fieldErrors.password ? true : undefined}
              aria-describedby={fieldErrors.password ? `${passwordId}-error` : undefined}
            />
          </Field>

          <Button
            type="submit"
            // With single sign-on above, that is the main way in; two primary
            // buttons on one card would leave nothing looking primary.
            variant={ssoOptions.length > 0 ? 'secondary' : 'primary'}
            size="lg"
            className="w-full justify-center"
            loading={signIn.isPending}
            // Never disabled for empty fields — submitting says what is missing.
            // Only while a rate limit is running, and then it says how long.
            disabled={secondsLeft > 0}
          >
            {secondsLeft > 0 ? `Try again in ${secondsLeft}s` : 'Sign in'}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}

/**
 * The reason an SSO attempt was refused, as the server put it.
 *
 * It arrives in the URL, which means anybody can put words on this page by
 * sending somebody a crafted link. React escapes the text, so there is no
 * injection — the risk is social: a convincing sentence next to a real sign-in
 * form is a phishing kit. So it is capped in length and dropped entirely if it
 * carries anything that could be a link or a phone number to call, neither of
 * which appears in a message this product actually sends.
 */
function readableSsoError(raw: string | null): string | null {
  if (!raw) return null;

  const text = raw.replace(/\s+/g, ' ').trim();
  if (text.length === 0 || text.length > 160) return null;

  const lowered = text.toLowerCase();
  const smellsLikeBait =
    lowered.includes('http') ||
    lowered.includes('://') ||
    lowered.includes('www.') ||
    lowered.includes('@') ||
    /\d[\d\s()+-]{6,}/.test(text);

  return smellsLikeBait ? 'Sign-in could not be completed.' : text;
}
